import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Tag,
  Typography,
  Button,
  Space,
  Popconfirm,
  Drawer,
  Descriptions,
  Empty,
  Spin,
  message,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { GoPlus } from "react-icons/go";
import { FiBox } from "react-icons/fi";
import { BiMoneyWithdraw, BiTrendingUp, BiTrendingDown } from "react-icons/bi";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction.js";
import { PERMISSIONS } from "../../../../config/permission.js";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading .js";
import {
  fetchAssetsThunk,
  deleteAssetThunk,
} from "../../../../Store/Slices/Finance/fixedAssets/fixedAssets.thunk";
import { clearFixedAssetsState } from "../../../../Store/Slices/Finance/fixedAssets/fixedAssets.slice";
import FixedAssetsForm from "./FixedAssetsForm";

const { Title, Text } = Typography;

const TYPE_COLOR = {
  Appreciating: "green",
  Depreciating: "red",
  Fixed: "blue",
};

const defaultDrawer = { open: false, mode: "add", record: null };

const MainSection = () => {
  const { t } = useTranslation("prtFinance");
  const dispatch = useDispatch();

  const role = useSelector((store) => store.common.auth.role);
  const { assets, loading, total, otherData, error } = useSelector(
    (store) => store.admin.fixedAssets
  );
  const schoolCurrency = useSelector(
    (store) => store.common.user.userDetails?.currency
  );

  const [drawer, setDrawer] = useState(defaultDrawer);

  useNavHeading(role, "Fixed Assets");

  useEffect(() => {
    dispatch(fetchAssetsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearFixedAssetsState());
    }
  }, [error, dispatch]);

  const openAdd = () => setDrawer({ open: true, mode: "add", record: null });
  const openEdit = (record) => setDrawer({ open: true, mode: "edit", record });
  const openView = (record) => setDrawer({ open: true, mode: "view", record });
  const closeDrawer = () => setDrawer(defaultDrawer);

  const handleDelete = (id) => {
    dispatch(deleteAssetThunk(id)).then((res) => {
      if (res?.payload?.success) message.success(t("Asset deleted"));
    });
  };

  // Top stat cards — inventory style
  const statCards = [
    {
      title: t("Total Assets"),
      value: `${total || 0} ${t("Items")}`,
      icon: <FiBox />,
    },
    {
      title: t("Total Value"),
      value: `${(otherData?.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${schoolCurrency || ""}`,
      icon: <BiMoneyWithdraw />,
    },
    {
      title: t("Appreciating"),
      value: `${otherData?.appreciating || 0} ${t("Items")}`,
      icon: <BiTrendingUp />,
    },
    {
      title: t("Depreciating"),
      value: `${otherData?.depreciating || 0} ${t("Items")}`,
      icon: <BiTrendingDown />,
    },
  ];

  return (
    <>
      <div className="p-4 bg-gray-50 min-h-screen">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-4 bg-white rounded-xl shadow-sm p-4">
          <div>
            <h1 className="page-heading">{t("Fixed Assets Management")}</h1>
            <p className="subheading">{t("Manage and track all your school assets")}</p>
          </div>
          <ProtectedAction requiredPermission={PERMISSIONS.ADD_FIXED_ASSETS}>
            <button
              onClick={openAdd}
              className="btn-gradient flex items-center gap-2 p-2"
            >
              <GoPlus /> {t("Add Asset")}
            </button>
          </ProtectedAction>
        </div>

        {/* TOP STAT CARDS — inventory style */}
        <div className="flex flex-row gap-4 mb-4">
          {statCards.map((item, index) => (
            <div
              key={index}
              className="p-5 w-full h-full rounded-2xl border border-purple-400 hover:scale-[101%] transition-transform duration-300 flex gap-4 bg-white shadow-md"
            >
              <div className="size-16 flex items-center justify-center rounded-2xl bg-gradient-to-r from-pink-600 to-violet-600 text-white text-3xl shadow-md">
                {item.icon}
              </div>
              <div className="flex flex-col">
                <h3 className="text-sm font-medium text-gray-600">{item.title}</h3>
                <h2 className="text-2xl font-bold text-purple-800">{item.value}</h2>
              </div>
            </div>
          ))}
        </div>

        {/* ASSET CARDS — liability style */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : !assets?.length ? (
          <Card className="!rounded-2xl !border-none">
            <Empty description={t("No assets found")} />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {assets.map((record) => (
              <Card
                key={record._id}
                className="rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300"
                styles={{ body: { padding: 20 } }}
              >
                {/* Card Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <Title level={5} style={{ margin: 0 }} className="capitalize">
                      {record.assetName}
                    </Title>
                    <Text type="secondary">{record?.category?.categoryName}</Text>
                  </div>
                  <Tag color={TYPE_COLOR[record.assetValueType] || "default"}>
                    {record.assetValueType || "Fixed"}
                  </Tag>
                </div>

                {/* Card Body */}
                <div className="space-y-2 mb-5">
                  <div className="flex justify-between">
                    <Text type="secondary">{t("Quantity")}</Text>
                    <Text strong>{record.quantity} {record.unit}</Text>
                  </div>
                  <div className="flex justify-between">
                    <Text type="secondary">{t("Unit Price")}</Text>
                    <Text strong>
                      {Number(record.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </div>
                  <div className="flex justify-between">
                    <Text type="secondary">{t("Current Value")}</Text>
                    <Text strong className="text-purple-700">
                      {Number(record.currentValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </div>
                  {record.assetValueType !== "Fixed" && (
                    <div className="flex justify-between">
                      <Text type="secondary">{t("Rate / Year")}</Text>
                      <Text strong>{record.ratePerYear}%</Text>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-100">
                  <Space wrap>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => openView(record)}
                    >
                      {t("View")}
                    </Button>

                    <ProtectedAction requiredPermission={PERMISSIONS.EDIT_FIXED_ASSETS}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                      >
                        {t("Edit")}
                      </Button>
                    </ProtectedAction>

                    <ProtectedAction requiredPermission={PERMISSIONS.DELETE_FIXED_ASSETS}>
                      <Popconfirm
                        title={t("Delete this asset?")}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t("Yes")}
                        cancelText={t("No")}
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          {t("Delete")}
                        </Button>
                      </Popconfirm>
                    </ProtectedAction>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* DRAWER */}
      <Drawer
        title={
          drawer.mode === "add"
            ? t("Add Asset")
            : drawer.mode === "edit"
              ? t("Edit Asset")
              : t("Asset Details")
        }
        open={drawer.open}
        onClose={closeDrawer}
        width={680}
      >
        {drawer.mode === "view" && drawer.record ? (
          <AssetViewDetails record={drawer.record} t={t} schoolCurrency={schoolCurrency} />
        ) : (
          <FixedAssetsForm
            onClose={closeDrawer}
            onSuccess={() => dispatch(fetchAssetsThunk())}
            editData={drawer.mode === "edit" ? drawer.record : null}
          />
        )}
      </Drawer>
    </>
  );
};


// View Details component shown in drawer
const AssetViewDetails = ({ record, t, schoolCurrency }) => (
  <Descriptions bordered column={1} size="middle">
    <Descriptions.Item label={t("Asset Name")}>
      {record.assetName || "-"}
    </Descriptions.Item>
    <Descriptions.Item label={t("Asset Code")}>
      {record.assetCode || "-"}
    </Descriptions.Item>
    <Descriptions.Item label={t("Category")}>
      {record?.category?.categoryName || "-"}
    </Descriptions.Item>
    <Descriptions.Item label={t("Value Type")}>
      <Tag color={TYPE_COLOR[record.assetValueType] || "default"}>
        {record.assetValueType || "Fixed"}
      </Tag>
    </Descriptions.Item>
    <Descriptions.Item label={t("Quantity")}>
      {record.quantity} {record.unit}
    </Descriptions.Item>
    <Descriptions.Item label={t("Unit Price")}>
      {Number(record.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {schoolCurrency}
    </Descriptions.Item>
    <Descriptions.Item label={t("Total Purchase Cost")}>
      {Number((record.unitPrice || 0) * (record.quantity || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })} {schoolCurrency}
    </Descriptions.Item>
    <Descriptions.Item label={t("Current Value")}>
      <span className="font-bold text-purple-700">
        {Number(record.currentValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {schoolCurrency}
      </span>
    </Descriptions.Item>
    <Descriptions.Item label={t("Rate (%) per Year")}>
      {record.ratePerYear || 0}%
    </Descriptions.Item>
    <Descriptions.Item label={t("Purchase Date")}>
      {record.purchaseDate ? dayjs(record.purchaseDate).format("DD MMM YYYY") : "-"}
    </Descriptions.Item>
    <Descriptions.Item label={t("Description")}>
      {record.description || "-"}
    </Descriptions.Item>
  </Descriptions>
);

export default MainSection;
